import React, { Component } from 'react';
import { Spin } from 'antd';
import _ from "lodash";
import html2canvas from "html2canvas";
import EventEmitter from 'events';

import Animator from './utils/Animator';
import Envelope from "./utils/Envelope";
import DiagramUtils from "./utils/DiagramUtils";
import Paper from './diagrams/Paper/Paper';
import PaperModel from "./diagrams/Paper/PaperModel";
import Selector from "./diagrams/Selector/Selector";
import Adorner from "./diagrams/Adorner/Adorner";
import DiagramsContainer from './containers/DiagramsContainer';
import PluginZoomPan from './plugins/PluginZoomPan';
import PluginSelection from './plugins/PluginSelection';
import PluginRectangleSelection from './plugins/PluginRectangleSelection';
import PluginProperties from './plugins/PluginProperties';
import PluginLayout from './plugins/PluginLayout';
import PluginDragAdd from './plugins/PluginDragAdd';
import PluginDragMove from './plugins/PluginDragMove';
import PluginShortcuts from './plugins/PluginShortcuts';
import PluginTestModels from './plugins/PluginTestModels';

import './DiagramDesigner.less';

export default class DiagramDesigner extends Component {

    state = {
        isBusy: false
    };

    id = `diagrams-designer-${Math.random()}`;
    initialized = false;
    dom = null;
    width = 0;
    height = 0;

    resolution = 1;
    extent = new Envelope({
        left: 0,
        top: 0,
        width: 0,
        height: 0,
    });

    plugins = [];
    diagrams = [];
    selectedItems = [];
    adorners = [];

    action = null;

    resolutionListeners = [];
    extentListeners = [];
    selectedItemsListeners = [];
    itemsChangedListeners = [];
    dataChangedListeners = {};
    actionChangedListeners = [];

    namedDiagrams = {};
    templates = {};

    paper = null;

    properties = { __FocusOnSelectedItemsChanged: true };

    eventEmitter = new EventEmitter({});

    constructor(props, context) {
        super(props, context);

        this.paper = new PaperModel({
            visible: false,
            x: 0,
            y: 0,
            width: props.paperWidth || 1366,
            height: props.paperHeight || 768,
            scale: 1,
        })
    }

    busy(isBusy = false) {
        this.setState({ isBusy });
    }

    cursor(cursor = "default") {
        this.setState({ cursor });
    }

    registerTemplate(id, template) {
        this.templates[id] = template;
    }

    unregisterTemplate(id) {
        delete this.templates[id];
    }

    registerNamedDiagram(name, ui, model) {
        this.namedDiagrams[name] = { ui, model };
    }

    unregisterNamedDiagram(name) {
        if (this.namedDiagrams[name])
            this.namedDiagrams[name] = undefined;
    }

    getNamedDiagrams() {

        const ds = {};
        for (const d of this.namedDiagrams) {
            if (!d)
                continue;
            if (!d.model.name)
                continue;

            ds[d.model.name] = d;
        }

        return ds;
    }

    registerActionChangedListener(listener) {
        this.actionChangedListeners.push(listener);

        return () => {
            const i = this.actionChangedListeners.indexOf(listener);
            if (i >= 0)
                this.actionChangedListeners.splice(i, 1);
        }
    }

    registerResolutionChangedListener(listener) {
        this.resolutionListeners.push(listener);

        return () => {
            const i = this.resolutionListeners.indexOf(listener);
            if (i >= 0)
                this.resolutionListeners.splice(i, 1);
        }
    }

    registerExtentChangedListener(listener) {
        this.extentListeners.push(listener);
        return () => {
            const i = this.extentListeners.indexOf(listener);
            if (i >= 0)
                this.extentListeners.splice(i, 1);
        }
    }

    registerSelectedItemsChangedListener(listener) {
        this.selectedItemsListeners.push(listener);
        return () => {
            const i = this.selectedItemsListeners.indexOf(listener);
            if (i >= 0)
                this.selectedItemsListeners.splice(i, 1);
        }
    }

    registerItemsChangedListener(listener) {
        this.itemsChangedListeners.push(listener);
        return () => {
            const i = this.itemsChangedListeners.indexOf(listener);
            if (i >= 0)
                this.itemsChangedListeners.splice(i, 1);
        }
    }

    raiseItemsChanged(val) {
        this.itemsChangedListeners.forEach(c => c(val));
    }

    registerDataChangedListener(name, listener) {

        const me = this;
        let list = this.dataChangedListeners[name];
        if (!list || !list.length) {
            this.dataChangedListeners[name] = list = [];
        }
        list.push(listener);

        return () => {
            const ls = me.dataChangedListeners;
            const i = list.indexOf(listener);
            if (i >= 0)
                list.splice(i, 1);
        }
    }

    raiseDataChanged(name, data) {
        let list = this.dataChangedListeners[name];
        if (list)
            list.forEach(c => c(data));

        if (this.props.messageDispatcher)
            this.props.messageDispatcher(name, data);
    }

    send(name, data) {
        this.raiseDataChanged(name, data);
    }

    received(name, listener) {
        this.registerDataChangedListener(name, listener);
    }

    dispatch(name, data) {
        this.raiseDataChanged(name, data);
    }

    on(name, listener) {
        this.registerDataChangedListener(name, listener);
    }

    setDesiginer(ref) {
        if (this.dom)
            return;

        this.dom = ref;
        setTimeout(() => {
            this.initialize();
        }, 0);
    }

    setEditor(ref) {
        if (this.domEdit)
            return;

        this.domEdit = ref;
    }

    initialize() {

        let dom = this.dom;
        let width = dom.clientWidth;
        let height = dom.clientHeight;

        this.width = width;
        this.height = height;
        this.mode = this.props.mode;

        this.pluginZoomPan = new PluginZoomPan(this, { animationSpeed: 550 });
        this.pluginZoomPan.install();
        this.plugins.push(this.pluginZoomPan);

        this.pluginProperties = new PluginProperties(this, {});
        this.pluginProperties.install();
        this.plugins.push(this.pluginProperties);

        this.pluginSelection = new PluginSelection(this, {});
        this.pluginSelection.install();
        this.plugins.push(this.pluginSelection);

        this.PluginRectangleSelection = new PluginRectangleSelection(this, {});
        this.PluginRectangleSelection.install();
        this.plugins.push(this.PluginRectangleSelection);

        this.pluginLayout = new PluginLayout(this, {});
        this.pluginLayout.install();
        this.plugins.push(this.pluginLayout);

        this.pluginDragAdd = new PluginDragAdd(this, {});
        this.pluginDragAdd.install();
        this.plugins.push(this.pluginDragAdd);

        this.PluginDragMove = new PluginDragMove(this, {});
        this.PluginDragMove.install();
        this.plugins.push(this.PluginDragMove);

        this.PluginShortcuts = new PluginShortcuts(this, {});
        this.PluginShortcuts.install();
        this.plugins.push(this.PluginShortcuts);

        if (this.props.plugins) {
            this.props.plugins.forEach(c => {
                const p = new c.plugin(this, {});
                p.install();
                this.plugins.push(p);
            })
        }

        // let plugin = new PluginTestModels(this, { total: 0 });
        // plugin.install();
        // this.plugins.push(plugin);

        this.paper.visible = true;
        if (this.props.paper) {
            for (const key in this.props.paper) {
                const value = this.props.paper[key];
                this.paper[key] = value;
            }
        }

        this.props.onInitialized && this.props.onInitialized();
        if (this.props.resetToPaperWhenInitialized !== false)
            this.setToModel(this.paper);
        else {
            this.setToNormal();
        }

        this.initialized = true;
    }

    componentWillMount() {
    }

    componentWillUnmount() {
        this.action = null;
        this.plugins.forEach(c => c.uninstall());
    }

    refresh() {
        this.setState({});
    }

    resize() {

        if (this.dom) {
            this.width = this.dom.clientWidth;
            this.height = this.dom.clientHeight;
        }

        // this.flyToPaper();
    }

    findById(id) {
        return this.paper.findById(id);
    }

    addModel(model, select = false, refresh = true) {

        this.diagrams = [...this.diagrams, model];
        if (select)
            this.selectedItems = model;
        if (refresh)
            this.refresh();

        this.properties.needSave = true;

        this.raiseItemsChanged(model);
    }

    insertModel(index, model, select = false, refresh = true) {

        this.diagrams.splice(index, 0, model)
        this.diagrams = [...this.diagrams];
        if (select)
            this.selectedItems = model;
        if (refresh)
            this.refresh();

        this.properties.needSave = true;

        this.raiseItemsChanged(model);
    }

    deleteModel(model, refresh = true) {
        this.deleteModels([model]);
        this.raiseItemsChanged(model);
    }

    deleteModels(models, refresh = true) {
        const items = [...models];
        items.forEach(c => {

            if (!c._sys.parent) {
                let i = this.diagrams.indexOf(c);
                if (i < 0)
                    return;

                this.diagrams.splice(i, 1);
            }
            else {
                c._sys.parent.deleteModel(c, true);
            }

            let i = this.selectedItems.indexOf(c);
            if (i >= 0) {
                this.selectedItems.splice(i, 1);
            }
        });

        this.diagrams = [...this.diagrams];
        if (refresh) {
            this.selectedItems = [...this.selectedItems];
        }

        this.properties.needSave = true;
        this.raiseItemsChanged(models);
    }

    setToModel(model) {
        this.pluginZoomPan.setToModel(model);
    }

    setToNormal() {
        this.pluginZoomPan.setToRect({
            left: 0,
            top: 0,
            width: this.width,
            height: this.height
        }, false);
    }

    setToRect(rect, buffer = true) {
        this.pluginZoomPan.setToRect(rect, buffer);
    }

    flyToPaper() {
        this.pluginZoomPan.flyToModel(this.paper);
    }

    flyToSelectedItems() {
        if (this.selectedItems.length == 0)
            this.flyToPaper();
        else
            this.flyToModels(this.selectedItems);
    }

    flyToAll() {
        if (this.diagrams.length == 0)
            this.flyToPaper();
        else
            this.flyToModels(this.diagrams);
    }

    flyToModel(model) {
        this.pluginZoomPan.flyToModel(model);
    }

    flyToModels(models) {
        this.pluginZoomPan.flyToModels(models);
    }

    flyToRect(rect, buffer = true) {
        this.pluginZoomPan.flyToRect(rect, buffer);
    }

    flyToResolution(res) {
        this.pluginZoomPan.flyToResolution(res);
    }

    alignItems(direction) {
        this.pluginLayout.alignItems(direction);
    }

    levelItems(direction) {
        this.pluginLayout.levelItems(direction);
    }

    groupItems(items) {
        this.pluginLayout.groupItems(items);
    }

    ungroupItems(items) {
        this.pluginLayout.ungroupItems(items);
    }

    createModelByModel(m) {
        let model = m;
        const diagrams = DiagramUtils.diagrams;
        const modelInfo = diagrams[model.type];
        if (!modelInfo)
            return;

        model = _.cloneDeep(model.model);
        model = new modelInfo.model(model);
        return model;
    }

    createModelByJson(json) {
        let model = JSON.parse(json);
        const diagrams = DiagramUtils.diagrams;
        const modelInfo = diagrams[model.type];
        if (!modelInfo)
            return;

        model = new modelInfo.model(model.model);
        return model;
    }

    createModelsByJson(json) {
        let arr = JSON.parse(json);
        const diagrams = DiagramUtils.diagrams;

        let models = [];
        arr.forEach(c => {

            const modelInfo = diagrams[c.type];
            if (!modelInfo)
                return;

            let model = new modelInfo.model(c.model);
            models.push(model);
        })

        return models;
    }

    absoluteScreenPointToView(point) {
        const rect = this.dom.getBoundingClientRect();
        let x = point.x - rect.left;
        let y = point.y - rect.top;
        let pt = this.screenPointToView({ x, y });
        return pt;
    }

    screenPointToView(point) {
        let x = this.extent.left + point.x * this.resolution;
        let y = this.extent.top + point.y * this.resolution;
        return { x, y };
    }

    toJson() {

        for (const key in this.templates) {
            if (!this.templates[key])
                delete this.templates[key];
        }

        let args = {
            view: {
                resolution: this.resolution,
                extent: { ...this.extent },
            },
            paper: this.paper.toJsonObject().model,
            diagrams: [],
            selection: [],
            templates: { ...this.templates },
        }

        this.diagrams.forEach(c => {
            args.diagrams.push(c.toJsonObject());
        });

        this.selectedItems.forEach(c => {
            args.selection.push(c.id);
        });

        let json = JSON.stringify(args);
        return json;
    }

    fromJson(json, select = true) {

        let args = JSON.parse(json);
        if (!args.diagrams)
            args.diagrams = [];
        if (!args.selection)
            args.selection = [];
        if (!args.templates)
            args.templates = {};

        this.paper.unlistenRefsPropertyChanged();
        this.paper.messages.forEach(c => { c.off(); });
        this.diagrams = [];

        if (args.paper) {
            let newPaper = new PaperModel({
                x: 0,
                y: 0,
                width: this.props.paperWidth || 1366,
                height: this.props.paperHeight || 768,
                scale: 1,
            });

            for (const key in newPaper) {
                if (key === "id")
                    continue;

                this.paper[key] = newPaper[key];
            }

            for (const key in args.paper) {
                if (key === "id")
                    continue;

                this.paper[key] = args.paper[key];
            }
        }

        this.paper.dataSources.forEach(c => {
            c._sys.__uiReady = true;
            // c._sys.__runScript = this.paperInstance.runScript.bind(this.paperInstance);
            // c._sys.__runActions = this.paperInstance.runActions.bind(this.paperInstance);

        });
        this.paper.messages.forEach(c => {
            c._sys.__designer = this;
            // c._sys.__runActions = this.paperInstance.runActions.bind(this.paperInstance);
        });
        this.paper.events.forEach(c => {
            c._sys.__designer = this;
            // c._sys.__model = this.paper;
            // c._sys.__runActions = this.paperInstance.runActions.bind(this.paperInstance);
        });

        this.templates = { ...args.templates };
        let ds = this.createModelsByJson(JSON.stringify(args.diagrams));
        let selectedItems = [];

        let models = [this.paper];
        ds.forEach(c => {
            models = [...models, ...c.expandModels()];
        });

        models.forEach(c => {
            c._sys.designer = this;

            let id = c.id;

            if (c !== this.paper) {
                c.setRandomIdSelf();
            }
            else {
                id = args.paper && args.paper.id;
            }

            if (!id)
                return;

            if (args.selection.indexOf(id) >= 0)
                selectedItems.push(c);

            const idNew = c.id;
            const modelInfo = {
                id: id,
                newId: idNew,
                instance: c,
            }

            models.forEach(m => {
                m.restoreRefs(modelInfo);
                // m.dataContext.refs.forEach(ref => {
                //     if (ref.id === id) {
                //         ref.id = idNew;
                //         ref._sys.ref = c;
                //     }
                // });
            });
        });

        models.forEach(m => {
            m.dataContext.refs.forEach(ref => {
                ref.enabled = !!ref._sys.ref;
            });
        });

        this.paper.listenRefsPropertyChanged();
        this.paper.messages.forEach(c => { c.on(); });
        this.paper.dataSources.forEach(c => { c.refreshInner(); });
        this.diagrams = ds;

        if (select)
            this.selectedItems = selectedItems;

        if (args.view && args.view.extent)
            this.setToRect(args.view.extent, false);

        this.raiseItemsChanged();
        // setTimeout(() => {
        //     this.refresh();
        // }, 1000);
    }

    newPaper() {

        let args = {
            paper: new PaperModel({
                x: 0,
                y: 0,
                width: this.props.paperWidth || 1366,
                height: this.props.paperWidth || 768,
                scale: 1,
            }),
        }

        let json = JSON.stringify(args);
        this.fromJson(json);
        this.flyToModel(this.paper);
    }

    getThumb() {

        return new Promise((resolve, reject) => {

            if (!(this.paperInstance && this.paperInstance.dom)) {
                reject();
                return;
            }

            var opts = {
                scale: .15,
            };

            html2canvas(this.paperInstance.dom, opts).then(canvas => {
                let base64 = canvas.toDataURL();

                resolve(JSON.stringify({
                    thumb: base64,
                    view: {
                        viewBackgroundColor: this.paper.viewBackgroundColor,
                        viewBackgroundImage: this.paper.viewBackgroundImage,
                    }
                }));

            }, e => reject(e)).catch(e => reject(e));
        })

    }

    viewPointToScreen(point) {
        let x = (point.x - this.extent.left) / this.resolution;
        let y = (point.y - this.extent.top) / this.resolution;
        return { x, y };
    }

    renderSelectors() {

        if (this.props.mode === "view")
            return;

        return this.selectedItems.map(c => {
            return <Selector key={`selector-${c.id}`} designer={this} id={c.id} model={c} />
        })
    }

    renderAdorners() {

        if (this.props.mode === "view")
            return;

        return this.adorners.map(c => {
            return <Adorner key={`adorner-${c.id}`} designer={this} id={c.id} model={c} />
        })
    }

    render() {

        const style = { ...this.paper.toViewStyle(), ...this.props.style };

        if (style.opacity === 1 || !("opacity" in style)) {
            style.opacity = this.state.isBusy ? .5 : 1
        }

        return (
            <div id={this.id} ref={ref => this.setDesiginer(ref)} tabIndex="0" className={"diagram-designer " + (this.props.mode === "view" ? "view-mode " : "") + (this.props.className || "")} style={{ ...style, cursor: this.state.cursor || "default" }}>
                {this.props.mode === "view" ? null : <div id={`edit-${this.id}`} ref={ref => this.setEditor(ref)} tabIndex="1" contentEditable={true} className="editdiv"></div>}
                <Paper ref={ref => this.paperInstance = ref || this.paperInstance} diagrams={this.diagrams} designer={this} model={this.paper} >
                </Paper>
                {this.renderSelectors()}
                {this.renderAdorners()}
                {
                    this.state.isBusy ?
                        <div className="layout-h center" style={{ backgroundColor: '#ffffff', opacity: .4, position: 'absolute', left: 0, top: 0, right: 0, bottom: 0 }} >
                            <Spin size="large" style={{ left: 0, zIndex: 100, right: 0, position: 'absolute', opacity: 1 }} />
                        </div > : null
                }
            </div>
        );
    }
}
